import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { todayLocal, fmtDate } from "@/lib/timefmt";
import { Check, Ban, Undo2, Lock, Repeat, MapPin, Trash2, Pencil, ListChecks } from "lucide-react";
import { useNavigate } from "react-router-dom";
import MembershipSetup from "@/components/MembershipSetup";
import { taskStatus, STATUS_META } from "@/lib/taskStatus";

const durMin = (t) => {
  const [sh, sm] = (t.start || "0:0").split(":").map(Number);
  const [eh, em] = (t.end || "0:0").split(":").map(Number);
  let v = (eh * 60 + em) - (sh * 60 + sm);
  if (v < 0) v += 1440;
  return v + (t.brief_min || 0) + (t.debrief_min || 0);
};
const fmtDur = (m) => `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;

export default function TaskListPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [cfg, setCfg] = useState({ modes: [], sequence_groups: [], sequences: [] });
  const [tasks, setTasks] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [replOpen, setReplOpen] = useState(false);
  const [listSearch, setListSearch] = useState("");
  const [listStatus, setListStatus] = useState("");
  const [listKind, setListKind] = useState("");
  const [listSort, setListSort] = useState("date_desc");
  const taskLabel = user?.terminology?.task_label || "Task";
  const tasksLabel = user?.terminology?.task_label_plural || "Tasks";
  const privilegedPost = (user?.privilege || "user") !== "user" || user?.role === "admin" || !!user?.admin_level;
  const canAuthorise = privilegedPost || !!user?.auth_officer;
  const todayStr = todayLocal();
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const postEligible = (t) => (t.end_date || t.date) <= todayStr
    && (t.status === "authorised" || t.status === "completed" || (t.status === "committed" && !t.requires_authorisation));

  const loadTasks = () => {
    const to = new Date();
    to.setDate(to.getDate() + 14);
    const from = new Date();
    from.setDate(from.getDate() - 13);
    const iso = (d) => d.toISOString().slice(0, 10);
    api.get(`/tasks?from_date=${iso(from)}&to_date=${iso(to)}`).then((r) => setTasks(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (!user?.company_id) return;
    api.get(`/task-config?company_id=${user.company_id}`).then((r) => setCfg(r.data)).catch(() => {});
    loadTasks();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const del = async (t) => {
    if (!window.confirm(`Delete ${t.task_type_name} on ${fmtDate(t.date)}?`)) return;
    try { await api.delete(`/tasks/${t.id}`); toast.success("Task deleted"); loadTasks(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const authorise = async (t, on) => {
    try {
      await api.post(`/tasks/${t.id}/authorise`, { on });
      toast.success(on ? "Task authorised" : "Authorisation revoked");
      loadTasks();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setStatus = async (t, status) => {
    try {
      await api.put(`/tasks/${t.id}`, {
        date: t.date, task_type_id: t.task_type_id, start: t.start, end: t.end,
        brief_min: t.brief_min ?? 0, debrief_min: t.debrief_min ?? 0, timezone: t.timezone,
        participant_ids: (t.participants || []).map((p) => p.user_id),
        mode_ids: t.mode_ids || [], sequence_ids: t.sequence_ids || [], equipment_ids: t.equipment_ids || [], status,
      });
      toast.success(status === "committed" ? "Task committed" : status === "cancelled" ? "Task cancelled" : "Task back to scheduled");
      loadTasks();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const modeName = (id) => cfg.modes.find((m) => m.id === id)?.name;
  const seqName = (id) => cfg.sequences.find((s) => s.id === id)?.name;

  const filtered = useMemo(() => tasks.filter((t) => {
    if (listSearch) {
      const q = listSearch.toLowerCase();
      if (!`${t.task_type_name} ${t.name || ""} ${t.remarks || ""}`.toLowerCase().includes(q)) return false;
    }
    if (listKind && t.kind !== listKind) return false;
    if (listStatus && taskStatus(t, null, todayStr, nowMin) !== listStatus) return false;
    return true;
  }), [tasks, listSearch, listKind, listStatus, todayStr, nowMin]);

  const flatSorted = [...filtered].sort((a, b) => {
    if (listSort === "type") return a.task_type_name.localeCompare(b.task_type_name) || b.date.localeCompare(a.date) || a.start.localeCompare(b.start);
    if (listSort === "status") return taskStatus(a, null, todayStr, nowMin).localeCompare(taskStatus(b, null, todayStr, nowMin)) || b.date.localeCompare(a.date);
    if (listSort === "date_asc") return a.date.localeCompare(b.date) || a.start.localeCompare(b.start);
    return b.date.localeCompare(a.date) || a.start.localeCompare(b.start);
  });
  const grouped = flatSorted.reduce((acc, t) => { (acc[t.date] = acc[t.date] || []).push(t); return acc; }, {});
  const dates = [...new Set(flatSorted.map((t) => t.date))];

  if (!user?.company_id || !user?.role_id) {
    return <MembershipSetup title={`${tasksLabel} List`} message="To see your tasks, join a company and pick your crew role. One-time setup." />;
  }

  return (
    <div data-testid="task-list-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Operations</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1">{`${taskLabel} List`}</h1>
        <p className="text-slate-400 mt-1 text-sm">Search, filter and manage your logged tasks — commit, authorise, post, replicate or edit (edits open on the Timeline page).</p>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="task-list-toolbar">
        <input value={listSearch} onChange={(e) => setListSearch(e.target.value)} placeholder="Search name, type, remarks…" data-testid="task-list-search"
          className="px-3 py-2.5 rounded-md bg-slate-900 border border-slate-800 text-sm flex-1 min-w-[180px]" />
        <select value={listKind} onChange={(e) => setListKind(e.target.value)} data-testid="task-list-kind"
          className="px-3 py-2.5 rounded-md bg-slate-900 border border-slate-800 text-sm">
          <option value="">All types</option>
          <option value="sortie">Sorties</option>
          <option value="commitment">Commitments</option>
        </select>
        <select value={listStatus} onChange={(e) => setListStatus(e.target.value)} data-testid="task-list-status"
          className="px-3 py-2.5 rounded-md bg-slate-900 border border-slate-800 text-sm">
          <option value="">All statuses</option>
          {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
        <select value={listSort} onChange={(e) => setListSort(e.target.value)} data-testid="task-list-sort"
          className="px-3 py-2.5 rounded-md bg-slate-900 border border-slate-800 text-sm">
          <option value="date_desc">Newest first</option>
          <option value="date_asc">Oldest first</option>
          <option value="type">By type</option>
          <option value="status">By status</option>
        </select>
        <label className="flex items-center gap-1.5 text-xs text-slate-400 px-1 cursor-pointer" title="Select all listed tasks">
          <input type="checkbox" className="accent-blue-600 h-3.5 w-3.5" data-testid="task-select-all"
            checked={flatSorted.length > 0 && flatSorted.every((t) => selected.has(t.id))}
            onChange={(e) => setSelected(e.target.checked ? new Set(flatSorted.map((t) => t.id)) : new Set())} />
          All
        </label>
        <button onClick={() => setReplOpen(true)} disabled={selected.size === 0} data-testid="task-replicate-open"
          title="Replicate the selected tasks to future dates"
          className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm disabled:opacity-40 transition-colors">
          <Repeat className="h-3.5 w-3.5" /> Replicate{selected.size ? ` (${selected.size})` : ""}
        </button>
      </div>

      <div className="space-y-4" data-testid="task-list">
        {dates.length === 0 && (
          <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60 text-sm text-slate-500">No tasks match.</div>
        )}
        {dates.map((d) => (
          <div key={d}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{fmtDate(d)}</div>
            </div>
            <div className="space-y-2">
              {grouped[d].map((t) => (
                <div key={t.id} className={`p-4 rounded-lg border bg-slate-900/60 ${t.status === "cancelled" ? "border-rose-400/50" : "border-slate-800"}`} data-testid={`task-card-${t.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <input type="checkbox" className="accent-blue-600 h-4 w-4 shrink-0" checked={selected.has(t.id)}
                          data-testid={`task-select-${t.id}`} onChange={() => toggleSelect(t.id)} />
                        <span className="font-medium text-sm">{t.name || t.task_type_name}</span>
                        {t.name && <span className="text-[10px] text-slate-500">{t.task_type_name}</span>}
                        {(() => {
                          const st = taskStatus(t, null, todayStr, nowMin);
                          const m = STATUS_META[st];
                          return <span data-testid={`task-status-${t.id}`} className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest border ${m.cls}`}>{m.label}</span>;
                        })()}
                        {t.kind === "commitment" && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest bg-violet-600/15 text-violet-300 border border-violet-600/40">Commitment</span>
                        )}
                        {t.fixed && <span title="Fixed — not moveable" className="inline-flex items-center"><Lock className="h-3 w-3 text-slate-500" /></span>}
                        <span className="text-xs font-mono-num text-slate-400">{t.start}–{t.end}</span>
                        {t.end_date && <span className="text-[10px] font-mono-num text-sky-400" data-testid={`task-enddate-${t.id}`}>→ {fmtDate(t.end_date)}</span>}
                        <span className="text-[10px] font-mono-num text-slate-500">+{t.brief_min}m brief / +{t.debrief_min}m debrief · {fmtDur(durMin(t))} total</span>
                      </div>
                      {t.remarks && <div className="text-[11px] text-slate-400 mt-1.5 italic" data-testid={`task-remarks-${t.id}`}>{t.remarks}</div>}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(t.mode_ids || []).map((id) => modeName(id) && (
                          <span key={id} className="px-2 py-0.5 rounded bg-blue-600/10 text-blue-300 border border-blue-600/30 text-[10px]">{modeName(id)}</span>
                        ))}
                        {(t.sequence_ids || []).map((id) => seqName(id) && (
                          <span key={id} className="px-2 py-0.5 rounded bg-violet-600/10 text-violet-300 border border-violet-600/30 text-[10px]">{seqName(id)}</span>
                        ))}
                        {(t.equipment_labels || []).map((l) => (
                          <span key={l} className="px-2 py-0.5 rounded bg-emerald-600/10 text-emerald-300 border border-emerald-600/30 text-[10px]">{l}</span>
                        ))}
                        {t.primary_location_name && (
                          <span data-testid={`task-loc-primary-${t.id}`} className="px-2 py-0.5 rounded bg-sky-600/10 text-sky-300 border border-sky-600/30 text-[10px] inline-flex items-center gap-1">
                            <MapPin className="h-2.5 w-2.5" /> {t.primary_location_name}
                          </span>
                        )}
                        {(t.additional_location_names || []).map((n) => (
                          <span key={n} className="px-2 py-0.5 rounded bg-sky-600/5 text-sky-400/90 border border-sky-600/20 text-[10px]">{n}</span>
                        ))}
                      </div>
                      {(t.risk_total || t.complexity_total) ? (
                        <div className="text-[10px] font-mono-num text-slate-500 mt-1.5">Risk {t.risk_total} · Complexity {t.complexity_total}</div>
                      ) : null}
                      <div className="text-[11px] text-slate-500 mt-1.5">
                        {t.created_by === user?.id ? "Logged by you" : `Logged by ${t.created_by_name}`}
                        {(t.participants || []).length > 0 && ` · with ${t.participants.map((p) => p.name).join(", ")}`}
                      </div>
                    </div>
                    {canAuthorise && t.requires_authorisation && t.status === "committed" && (
                      <button onClick={() => authorise(t, true)} data-testid={`task-authorise-${t.id}`} title="Authorise task (required before post-task)"
                        className="px-2.5 py-2 rounded-md bg-violet-600/15 border border-violet-600/40 text-violet-300 text-xs font-medium hover:bg-violet-600/25 transition-colors self-center shrink-0">Authorise</button>
                    )}
                    {t.requires_authorisation && t.status === "authorised" && (
                      <button onClick={canAuthorise ? () => authorise(t, false) : undefined} data-testid={`task-unauthorise-${t.id}`}
                        title={canAuthorise ? "Revoke authorisation" : "Authorised"}
                        className="px-2.5 py-2 rounded-md bg-violet-600/25 border border-violet-500/50 text-violet-200 text-xs font-medium hover:bg-violet-600/35 transition-colors self-center shrink-0">Authorised ✓</button>
                    )}
                    {(t.created_by === user?.id || (t.participants || []).some((p) => p.user_id === user?.id) || privilegedPost) && postEligible(t) && (
                      <button onClick={() => nav(`/post-task?task=${t.id}`)} data-testid={`task-post-${t.id}`} title={`Post ${taskLabel.toLowerCase()} — verify & complete`}
                        className="px-2.5 py-2 rounded-md bg-blue-600/15 border border-blue-600/40 text-blue-300 text-xs font-medium hover:bg-blue-600/25 transition-colors self-center shrink-0">Post</button>
                    )}
                    {t.created_by === user?.id && (
                      <div className="flex items-center gap-2 shrink-0">
                        {t.status !== "committed" && t.status !== "cancelled" && taskStatus(t, null, todayStr, nowMin) !== "completed" && (
                          <button onClick={() => setStatus(t, "committed")} data-testid={`task-commit-${t.id}`} title="Commit task"
                            className="p-2 rounded-md bg-slate-800 hover:bg-green-900/40 text-slate-300 hover:text-green-300 transition-colors"><Check className="h-3.5 w-3.5" /></button>
                        )}
                        {t.status === "committed" && (
                          <button onClick={() => setStatus(t, "scheduled")} data-testid={`task-uncommit-${t.id}`} title="Back to scheduled"
                            className="p-2 rounded-md bg-slate-800 hover:bg-slate-700 text-green-300 transition-colors"><Undo2 className="h-3.5 w-3.5" /></button>
                        )}
                        {t.status !== "cancelled" && (
                          <button onClick={() => setStatus(t, "cancelled")} data-testid={`task-cancel-${t.id}`} title="Cancel task"
                            className="p-2 rounded-md bg-slate-800 hover:bg-rose-900/40 text-slate-300 hover:text-rose-300 transition-colors"><Ban className="h-3.5 w-3.5" /></button>
                        )}
                        <button onClick={() => nav(`/tasks?edit=${t.id}`)} data-testid={`task-edit-${t.id}`} title="Edit on the Timeline page"
                          className="p-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
                        <button onClick={() => del(t)} data-testid={`task-delete-${t.id}`}
                          className="p-2 rounded-md bg-slate-800 hover:bg-rose-900/40 text-slate-300 hover:text-rose-300 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <ReplicateDialog open={replOpen} tasks={tasks.filter((t) => selected.has(t.id))} today={todayStr}
        onClose={() => setReplOpen(false)} onDone={() => { setReplOpen(false); setSelected(new Set()); loadTasks(); }} />
    </div>
  );
}

function ReplicateDialog({ open, tasks, today, onClose, onDone }) {
  const [selDates, setSelDates] = useState([]);
  const [keepCrew, setKeepCrew] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setSelDates([]);
      setKeepCrew(true);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!open) return null;
  const p2 = (x) => String(x).padStart(2, "0");
  const futureDates = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(`${today}T00:00:00`);
    d.setDate(d.getDate() + i + 1);
    return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
  });
  const toggle = (list, setList, v) => setList(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const submit = async () => {
    if (!tasks.length) { toast.error("Select at least one task in the list first"); return; }
    if (!selDates.length) { toast.error("Select at least one date"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/tasks/replicate", { task_ids: tasks.map((t) => t.id), dates: selDates, keep_participants: keepCrew });
      toast.success(`Created ${data.created} task(s) across ${selDates.length} day(s)`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="replicate-dialog">
        <DialogHeader><DialogTitle className="font-heading">Replicate {tasks.length} selected task(s)</DialogTitle></DialogHeader>
        <div className="space-y-1 mb-4">
          {tasks.map((t) => (
            <div key={t.id} className="text-sm text-slate-300 truncate" data-testid={`replicate-task-${t.id}`}>
              {fmtDate(t.date)} · {t.name || t.task_type_name} <span className="text-xs text-slate-500 font-mono-num">{t.start}–{t.end}</span>
            </div>
          ))}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Replicate to dates (max 14 days ahead)</div>
        <div className="grid grid-cols-2 gap-1 mb-4">
          {futureDates.map((d) => (
            <label key={d} className="flex items-center gap-2 text-xs text-slate-300">
              <input type="checkbox" className="accent-blue-600 h-3.5 w-3.5" checked={selDates.includes(d)}
                data-testid={`replicate-date-${d}`} onChange={() => toggle(selDates, setSelDates, d)} />
              {fmtDate(d)}
            </label>
          ))}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-1.5">Crew</div>
        <div className="flex gap-4 mb-2 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="radio" name="repl-crew" className="accent-blue-600" checked={keepCrew} onChange={() => setKeepCrew(true)} data-testid="replicate-keep-crew" />
            Keep crew (copy participants)
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="radio" name="repl-crew" className="accent-blue-600" checked={!keepCrew} onChange={() => setKeepCrew(false)} data-testid="replicate-clear-crew" />
            Clear crew
          </label>
        </div>
        <button onClick={submit} disabled={busy} data-testid="replicate-confirm"
          className="mt-3 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium disabled:opacity-50">
          {busy ? "Replicating…" : "Replicate tasks"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
